import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './DossiersTable.scss';
import { ESortDirection, ResponsiveTable, ResponsiveTableColumn } from '../responsive-table/ResponsiveTable';
import Button from 'react-bootstrap/Button';
import { Search } from '../search/Search';
import { sessionActions } from '../../app/session/actions';
import { ERouterPath } from '../router/ERouterPath';
import { useHistory } from 'react-router';
import { CaregiversList } from '../caregivers-list/CaregiversList';
import { DossierV2, IDossierWithOptionalRelation } from '../../models/DossierV2';
import { DossierMember } from '../../models/DossierMember';
import { Badge } from '../badge/Badge';
import { MultiDossierUnreadStatus } from '../../models/UnreadStatus';
import { SingleUser, UserAccountRoleEnum } from '../../api/types';
import { Api } from '../../api';
import { ApplicationState } from '../../app/store';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { ConfirmationButton } from '../confirmation-button/ConfirmationButton';
import ButtonGroup from 'react-bootstrap/esm/ButtonGroup';

interface IDossiersTableProps {
  dossiersWithRelation: IDossierWithOptionalRelation[];
  badges: MultiDossierUnreadStatus;
  onArchiveDossier?: (dossier: DossierV2) => void;
  onUnarchiveDossier?: (dossier: DossierV2) => void;
}

interface IDossiersTableData {
  id: number;
  createdAt: string;
  lastActivityAt: string;
  dossierName: JSX.Element;
  caregivers: JSX.Element;
  buttons: JSX.Element;
}

export function DossiersTable({ dossiersWithRelation, badges, onArchiveDossier, onUnarchiveDossier }: IDossiersTableProps): JSX.Element {
  const [searchKeys, setSearchKeys] = useState<string | undefined>(undefined);
  const user = useSelector<ApplicationState, SingleUser|undefined>(state => state.session.user);
  const history = useHistory();
  const dispatch = useDispatch();
  const setActiveDossier = useCallback(
    (dossier: DossierV2, dossierRelations: DossierMember[], relation?: DossierMember) => dispatch(sessionActions.setDossier({ dossier, relation, dossierRelations })),
    [dispatch]
  );
  const [displayArchived, setDisplayArchived] = useState(false);

  const filter = ({ dossier }: IDossierWithOptionalRelation): boolean => {
    if (
      (displayArchived && !dossier.archived) ||
      (!displayArchived && dossier.archived)
    ) {
      return false;
    }

    if (!searchKeys || typeof searchKeys !== 'string') {
      return true;
    }
    const sanitizedKeys = searchKeys?.trim().toLowerCase();
    if (!sanitizedKeys || sanitizedKeys.length === 0) {
      return true;
    } else {
      return dossier.name.toLowerCase().includes(sanitizedKeys);
    }
  };

  const filteredDossierWithRelation = useMemo(() => dossiersWithRelation.filter(filter), [dossiersWithRelation, displayArchived, searchKeys]);

  const isAdmin = user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration].includes(user.accountRole);

  const sort = (indexA: number, indexB: number, direction: ESortDirection, objectKey: keyof IDossiersTableData): number => {
    const a = filteredDossierWithRelation[indexA]?.dossier;
    const b = filteredDossierWithRelation[indexB]?.dossier;
    // The ?. above and this check below should not be necessary, but it might fix a weird bug on Safari? Let's log for now since I'm not abel to reproduce.
    if (!a || !b) {
      console.warn(`Sort in DossierTable error, sort item a: ${a ? 'defined' : 'undefined'} b: ${b ? 'defined' : 'undefined'}`);
      return 0;
    }

    const sortDesc = direction === ESortDirection.DESCENDING;
    switch (objectKey) {
      case 'id':
        return sortDesc ? a.id - b.id : b.id - a.id;
      case 'dossierName':
        return sortDesc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      case 'createdAt':
        return sortDesc ? b.createdAt.valueOf() - a.createdAt.valueOf() : a.createdAt.valueOf() - b.createdAt.valueOf();
      case 'lastActivityAt':
        return sortDesc ? b.lastActivityAt.valueOf() - a.lastActivityAt.valueOf() : a.lastActivityAt.valueOf() - b.lastActivityAt.valueOf();
      default:
        return 0;
    }
  };

  const columns: ResponsiveTableColumn<IDossiersTableData>[] = [
    { title: 'ID', objectKey: 'id', customSortFunction: sort },
    { title: 'Naam van dossier', objectKey: 'dossierName', customSortFunction: sort },
    { title: 'Aangemaakt op', objectKey: 'createdAt', customSortFunction: sort },
    { title: 'Laatste activiteit op', objectKey: 'lastActivityAt', customSortFunction: sort },
    { title: 'Behandelaar(s)', objectKey: 'caregivers', minWidth: '12rem', disableSort: true },
    { title: '', objectKey: 'buttons', minWidth: '18rem', disableSort: true },
  ];

  const goToFile = ({ dossier, relation, dossierRelations }: IDossierWithOptionalRelation): void => {
    setActiveDossier(dossier, dossierRelations, relation);
    history.push(ERouterPath.dossier);
  };

  const archiveDossier = (dossier: DossierV2): void => {
    if (!onArchiveDossier) {
      return;
    }

    Api.archiveDossier(dossier.id)
      .then(() => {
        NotificationHelper.toastSuccess('Dossier gearchiveerd');
        onArchiveDossier(dossier);
      })
      .catch(() => {
        NotificationHelper.toastError('Dossier archiveren mislukt');
      });
  };

  const unarchiveDossier = (dossier: DossierV2): void => {
    if (!onUnarchiveDossier) {
      return;
    }

    Api.unarchiveDossier(dossier.id)
      .then(() => {
        NotificationHelper.toastSuccess('Dossier uit archief gehaald');
        onUnarchiveDossier(dossier);
      })
      .catch(() => {
        NotificationHelper.toastError('Dossier uit archief halen mislukt');
      });
  };

  const handleSearch = (value: string): void => {
    setSearchKeys(value);
  };

  const renderArchiveButton = (dossier: DossierV2): JSX.Element|null => {
    if (!isAdmin) {
      return null;
    }

    if (dossier.archived && onUnarchiveDossier) {
      return <ConfirmationButton
        variant="link"
        className="responsive-table__button"
        modalTitle="Dossier archief halen"
        modalBody={`Dossier "${dossier.name}" uit het archief halen? De personen in het dossier kunnen dan weer berichten plaatsen en bestanden uploaden.`}
        modalConfirmLabel="Uit archief halen"
        onClick={() => unarchiveDossier(dossier)}>
        Uit archief halen
      </ConfirmationButton>;
    } else if (!dossier.archived && onArchiveDossier) {
      return <ConfirmationButton
        variant="link"
        className="responsive-table__button"
        modalTitle="Dossier archiveren"
        modalBody={`Dossier "${dossier.name}" verplaatsen naar het archief? Personen in het dossier hebben dan nog wel toegang tot geplaatste berichten en bestanden, maar kunnen niks nieuws meer plaatsen.`}
        modalConfirmLabel="Archiveren"
        onClick={() => archiveDossier(dossier)}>
        Archiveren
      </ConfirmationButton>;
    }

    return null;
  };

  const data: IDossiersTableData[] = filteredDossierWithRelation.map(({ dossier, dossierRelations, relation }) => {
    const badgeCount = badges[dossier.id]?.total || 0;
    return {
      id: dossier.id,
      createdAt: dossier.getFormattedCreatedAt(),
      lastActivityAt: dossier.getFormattedLastActivityAt(),
      dossierName: <>{dossier.name}<Badge className="dossier-table__badge" count={badgeCount}/></>,
      caregivers: <CaregiversList dossierId={dossier.id}/>,
      buttons: <>
        <div className="files-table__buttons">
          <Button variant="primary" className="responsive-table__button" onClick={() => {
            goToFile({ dossier, dossierRelations, relation });
          }}>Naar het dossier</Button>
          { renderArchiveButton(dossier) }
        </div>
      </>
    };
  });

  return (
    <div className="files-table">
      <div className="sort-search-container">
        <Search onSearch={handleSearch} inverted={true} placeholder="Zoek op naam van dossier"/>
        <ButtonGroup>
          <Button className="files-table__archived-switch-button" variant="primary" onClick={() => setDisplayArchived(false)} disabled={!displayArchived}>Actief</Button>
          <Button className="files-table__archived-switch-button" variant="primary" onClick={() => setDisplayArchived(true)} disabled={displayArchived}>Gearchiveerd</Button>
        </ButtonGroup>
      </div>
      <ResponsiveTable<IDossiersTableData> columns={columns} data={data} sortByDefault={columns[3]} whiteRows={true}/>
    </div>
  );
}
